/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useContext } from "react";
import { ILayoutContext } from "../ui/components/Layout/LayoutContext";

export interface IInspectorContext {
	getInspectorRect?: () => {
		width: number;
		height: number;
		x: number;
		y: number;
	};
}

const defaultContext: IInspectorContext = {};

export const InspectorContext = React.createContext<IInspectorContext>(defaultContext);

export function useInspectorContext(): IInspectorContext {
	return useContext(InspectorContext);
}

export interface IInspectorContextProviderProps {
	inspectorElRef?: React.MutableRefObject<HTMLElement>;
	children?: React.ReactNode;
}

export const InspectorContextProvider: React.FC<IInspectorContextProviderProps> = ({
	children,
	inspectorElRef
}) => {
	const upperContext = useInspectorContext();

	const getInspectorRect = useCallback<ILayoutContext["getLayoutRect"]>(() => {
		const { width, height, x, y } = inspectorElRef.current?.getBoundingClientRect() || {
			width: 0,
			height: 0,
			x: 0,
			y: 0
		};
		return {
			x,
			y,
			width,
			height
		};
	}, [ inspectorElRef ]);

	return (
		<InspectorContext.Provider
			value={{ getInspectorRect: upperContext?.getInspectorRect || getInspectorRect }}
		>
			{children}
		</InspectorContext.Provider>
	);
};
